import {
  BoxGeometry,
  BufferGeometry,
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  Float32BufferAttribute,
  IcosahedronGeometry,
  LatheGeometry,
  OctahedronGeometry,
  Matrix4,
  PlaneGeometry,
  SphereGeometry,
  TetrahedronGeometry,
  Vector2,
} from 'three';
import { GeoBuilder, hash01, type Proto, trs } from '../geo';
import { look } from '../look';
import { mix, PAL } from '../palette';

/**
 * The scenery kit: every piece is a few simple, chunky shapes (spec: cypress
 * cones, umbrella pines as discs on trunks, hay bales as cylinders, stone walls
 * as blocks...). Each is built once as a prototype facing +x and stamped into
 * merged meshes.
 */

/** The setting's colours (look.ts) are read as each piece is built, so a world built in it gets them. */
const trees = () => look().trees;
const houses = () => look().houses;
const STONE_A = mix('asphalt', 'cream', 0.3);
const CONCRETE = mix('cream', 'asphalt', 0.35);

function jitter(geo: BufferGeometry, amount: number, seed: number): BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  const p = g.getAttribute('position');
  // Jitter shared corners identically so the surface stays closed.
  const key = (x: number, y: number, z: number): number => Math.round(x * 1000) * 73856093 ^ Math.round(y * 1000) * 19349663 ^ Math.round(z * 1000) * 83492791;
  const cache = new Map<number, [number, number, number]>();
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const k = key(x, y, z);
    let d = cache.get(k);
    if (!d) {
      d = [(hash01(k, seed) - 0.5) * amount, (hash01(k, seed + 1) - 0.5) * amount, (hash01(k, seed + 2) - 0.5) * amount];
      cache.set(k, d);
    }
    p.setXYZ(i, x + d[0], y + d[1], z + d[2]);
  }
  p.needsUpdate = true;
  return g;
}

function lathe(points: [number, number][], segments: number): LatheGeometry {
  return new LatheGeometry(points.map(([r, y]) => new Vector2(r, y)), segments);
}

// ---------------------------------------------------------------------------
// Trees and plants
// ---------------------------------------------------------------------------

export function cypressProto(v: number): Proto {
  const b = new GeoBuilder();
  const w = [1, 0.86, 1.1][v % 3];
  const h = [1, 1.14, 0.9][v % 3];
  b.add(new CylinderGeometry(0.15, 0.22, 1.1, 5), trees().trunk, trs(0, 0.55, 0));
  const prof: [number, number][] = [
    [0.001, 0.5],
    [0.72 * w, 0.9],
    [1.12 * w, 2.2],
    [1.22 * w, 3.9],
    [1.08 * w, 5.9],
    [0.78 * w, 7.7],
    [0.42 * w, 9.2],
    [0.001, 10.7],
  ].map(([r, y]) => [r, y * h] as [number, number]);
  b.add(jitter(lathe(prof, 7), 0.18, 11 + v), trees().cypress);
  return b.toProto();
}

export function pineProto(v: number): Proto {
  const b = new GeoBuilder();
  const lean = [0.07, -0.1, 0.13][v % 3];
  const trunkH = [7.6, 8.6, 6.8][v % 3];
  const m = new Matrix4().makeRotationZ(lean).multiply(trs(0, trunkH / 2, 0));
  b.add(new CylinderGeometry(0.26, 0.42, trunkH, 6), trees().trunk, m);
  const topX = -Math.sin(lean) * trunkH;
  const topY = Math.cos(lean) * trunkH;
  // Two or three branches splaying out under the canopy.
  const branches = v % 3 === 1 ? 3 : 2;
  for (let k = 0; k < branches; k++) {
    const a = (k / branches) * Math.PI * 2 + v;
    const bm = new Matrix4()
      .makeTranslation(topX, topY - 0.4, 0)
      .multiply(new Matrix4().makeRotationY(a))
      .multiply(new Matrix4().makeRotationZ(-0.75))
      .multiply(trs(0, 1.3, 0));
    b.add(new CylinderGeometry(0.13, 0.2, 2.8, 5), trees().trunk, bm);
  }
  // Flat, faceted canopy: a squashed icosahedron plus a second lobe.
  const canopy = trees().canopy;
  const main = jitter(new IcosahedronGeometry(1, 1), 0.22, 40 + v);
  b.add(main, canopy, trs(topX, topY + 1.7, 0, v, 5.6, 1.35, 5.1));
  const lobe = jitter(new IcosahedronGeometry(1, 1), 0.25, 50 + v);
  b.add(lobe, canopy, trs(topX + 2.2, topY + 1.35, 1.4, v * 2, 3.1, 1.0, 2.9));
  if (v % 3 !== 2) b.add(jitter(new IcosahedronGeometry(1, 0), 0.2, 60 + v), canopy, trs(topX - 2.4, topY + 1.2, -1.5, v, 2.6, 0.85, 2.4));
  return b.toProto();
}

export function oliveProto(v: number): Proto {
  const b = new GeoBuilder();
  // Short, twisted trunk in two pieces.
  b.add(new CylinderGeometry(0.28, 0.45, 1.5, 6), trees().trunk, new Matrix4().makeRotationZ(0.18).multiply(trs(0, 0.75, 0)));
  b.add(new CylinderGeometry(0.2, 0.28, 1.2, 5), trees().trunk, new Matrix4().makeRotationZ(-0.3).multiply(trs(0.3, 1.9, 0)));
  const leaf = v === 2 ? trees().leafDark : trees().leaf;
  const blobs: [number, number, number, number][] = [
    [0, 2.9, 0, 1.5],
    [1.1, 2.6, 0.6, 1.1],
    [-0.9, 2.5, -0.5, 1.15],
    [0.3, 3.4, -0.9, 1.0],
  ];
  blobs.forEach(([x, y, z, r], k) => b.add(jitter(new DodecahedronGeometry(1, 0), 0.25, 70 + v * 5 + k), leaf, trs(x, y, z, k + v, r, r * 0.85, r)));
  return b.toProto();
}

export function bushProto(v: number): Proto {
  const b = new GeoBuilder();
  const col = v === 1 ? trees().bushDark : trees().bush;
  b.add(jitter(new DodecahedronGeometry(1, 0), 0.25, 90 + v), col, trs(0, 0.55, 0, v, 1.1, 0.8, 1.0));
  b.add(jitter(new DodecahedronGeometry(1, 0), 0.25, 95 + v), col, trs(0.8, 0.4, 0.3, v, 0.7, 0.55, 0.7));
  if (v !== 2) b.add(jitter(new DodecahedronGeometry(1, 0), 0.25, 99 + v), col, trs(-0.6, 0.38, -0.4, v, 0.65, 0.5, 0.65));
  return b.toProto();
}

/** Grass tufts at the foot of walls and boards. */
// The other settings' trees (look.ts): what the plan's cypresses, pines, olives and bushes become there.

/** A spruce (the Dolomites): tiers of dark cones on a short trunk. */
export function spruceProto(v: number): Proto {
  const b = new GeoBuilder();
  const w = [1, 0.86, 1.12][v % 3];
  const h = [1, 1.18, 0.88][v % 3];
  b.add(new CylinderGeometry(0.22, 0.32, 2.2, 5), trees().trunk, trs(0, 1.1, 0));
  const col = v === 1 ? trees().canopy : trees().cypress;
  const tiers: [number, number, number][] = [
    [2.7, 4.6, 1.6],
    [2.2, 4.1, 3.9],
    [1.7, 3.7, 6.0],
    [1.15, 3.2, 8.0],
    [0.6, 2.4, 9.9],
  ];
  tiers.forEach(([r, th, y], k) => b.add(jitter(new ConeGeometry(r * w, th * h, 6), 0.16, 120 + v * 7 + k), col, trs(0, (y + th / 2) * h, 0)));
  return b.toProto();
}

/** A larch (the Dolomites in October): a slimmer cone gone gold. */
export function larchProto(v: number): Proto {
  const b = new GeoBuilder();
  const h = [1, 1.12, 0.9][v % 3];
  b.add(new CylinderGeometry(0.18, 0.28, 2.6, 5), trees().trunk, trs(0, 1.3, 0));
  const col = v === 2 ? trees().leafDark : trees().leaf;
  const prof: [number, number][] = [
    [0.001, 1.6],
    [1.5, 2.6],
    [1.75, 4.6],
    [1.35, 7.4],
    [0.7, 9.6],
    [0.001, 11.4],
  ].map(([r, y]) => [r, y * h] as [number, number]);
  b.add(jitter(lathe(prof, 7), 0.22, 140 + v), col);
  return b.toProto();
}

/**
 * A palm (Liguria's seafront, Sicily): a leaning ringed trunk, drooping fronds. Kept to a cypress's
 * triangles (it stands in for one): open trunk sections, each frond a flat three-sided blade.
 */
export function palmProto(v: number): Proto {
  const b = new GeoBuilder();
  const lean = [0.12, -0.08, 0.2][v % 3];
  const tall = [8.5, 10, 7.2][v % 3];
  let x = 0;
  let y = 0;
  const segs = 3;
  for (let k = 0; k < segs; k++) {
    const a = lean * (0.4 + k / segs);
    const len = tall / segs;
    const m = new Matrix4().makeTranslation(x, y, 0).multiply(new Matrix4().makeRotationZ(-a)).multiply(trs(0, len / 2, 0));
    b.add(new CylinderGeometry(0.22 - k * 0.03, 0.3 - k * 0.03, len * 1.04, 5, 1, true), k % 2 ? trees().trunk : mix('brown', 'cream', 0.42), m);
    x += Math.sin(a) * len;
    y += Math.cos(a) * len;
  }
  const frond = trees().bushDark;
  const n = 6;
  for (let k = 0; k < n; k++) {
    const yaw = (k / n) * Math.PI * 2 + v;
    // A long blade out from the crown, curving down (two flattened three-sided sections).
    const base = new Matrix4().makeTranslation(x, y, 0).multiply(new Matrix4().makeRotationY(yaw));
    const inner = base.clone().multiply(new Matrix4().makeRotationZ(-Math.PI / 2 + 0.3)).multiply(trs(0, 1.5, 0, 0, 1, 1, 0.25));
    b.add(new CylinderGeometry(0.5, 0.75, 3.0, 3, 1, true), frond, inner);
    const outer = base
      .clone()
      .multiply(new Matrix4().makeRotationZ(0.3))
      .multiply(new Matrix4().makeTranslation(2.85, 0, 0))
      .multiply(new Matrix4().makeRotationZ(-Math.PI / 2 - 0.75))
      .multiply(trs(0, 1.2, 0, 0, 1, 1, 0.25));
    b.add(new CylinderGeometry(0.05, 0.5, 2.4, 3, 1, true), frond, outer);
  }
  b.add(new OctahedronGeometry(0.5, 0), mix('brown', 'terracotta', 0.4), trs(x, y - 0.15, 0));
  return b.toProto();
}

/** A prickly pear (Sicily): a clump of flat pads, a few fruits on the rims (a bush's triangles, no more). */
export function cactusProto(v: number): Proto {
  const b = new GeoBuilder();
  const pad = v === 1 ? trees().bushDark : trees().bush;
  const fruit = new Color('#d8703e');
  const pads: [number, number, number, number, number][] = [
    // x, y, z, yaw, tilt
    [0, 0.75, 0, 0, 0],
    [0.7, 0.85, 0.35, 1.2, -0.3],
    [-0.65, 0.8, -0.3, 2.4, 0.35],
    [0.2, 1.85, 0.1, 0.5, 0.25],
    [-0.35, 1.75, -0.15, 1.9, -0.35],
    [0.85, 1.75, 0.45, 2.8, -0.5],
    [0.05, 2.75, 0.1, 1.1, 0.1],
  ];
  pads.slice(0, v === 2 ? 5 : 7).forEach(([x, y, z, yaw, tilt], k) => {
    const m = new Matrix4().makeTranslation(x, y, z).multiply(new Matrix4().makeRotationY(yaw)).multiply(new Matrix4().makeRotationZ(tilt));
    b.add(jitter(new OctahedronGeometry(1, 0), 0.1, 160 + v * 9 + k), pad, m.multiply(trs(0, 0, 0, 0, 0.62, 0.8, 0.22)));
    if (k >= 3 && k % 2 === 1) b.add(new TetrahedronGeometry(0.17, 0), fruit, new Matrix4().makeTranslation(x + 0.15, y + 0.74, z));
  });
  return b.toProto();
}

/** An oleander or a bougainvillea (Liguria): a bush in flower (a bush's triangles, no more). */
export function oleanderProto(v: number): Proto {
  const b = new GeoBuilder();
  const col = v === 1 ? trees().bushDark : trees().bush;
  const flower = new Color(['#e29c9a', '#f0e4d2', '#c5527b'][v % 3]);
  b.add(jitter(new DodecahedronGeometry(1, 0), 0.25, 170 + v), col, trs(0, 0.8, 0, v, 1.3, 1.05, 1.2));
  b.add(jitter(new OctahedronGeometry(1, 0), 0.2, 175 + v), col, trs(0.95, 0.6, 0.35, v, 0.85, 0.75, 0.85));
  // (The third bush it stands in for is lighter: one flower fewer.)
  for (let k = 0; k < (v % 3 === 2 ? 7 : 8); k++) {
    const a = hash01(v, k) * Math.PI * 2;
    const e = 0.25 + hash01(v + 3, k) * 0.9;
    const r = 1.18;
    b.add(new TetrahedronGeometry(0.26, 0), flower, new Matrix4().makeTranslation(Math.cos(a) * r * Math.cos(e) * 1.1, 0.8 + Math.sin(e) * r * 0.95, Math.sin(a) * r * Math.cos(e)));
  }
  return b.toProto();
}

/** A lemon tree (Sicily): a dense round crown with the fruit showing (an olive's triangles, no more). */
export function citrusProto(v: number): Proto {
  const b = new GeoBuilder();
  b.add(new CylinderGeometry(0.2, 0.3, 1.4, 5, 1, true), trees().trunk, trs(0, 0.7, 0));
  const leaf = trees().leafDark;
  b.add(jitter(new DodecahedronGeometry(1, 0), 0.2, 180 + v), leaf, trs(0, 2.3, 0, v, 1.75, 1.45, 1.7));
  b.add(jitter(new DodecahedronGeometry(1, 0), 0.2, 184 + v), leaf, trs(0.7, 2.9, 0.3, v + 1, 1.1, 0.95, 1.1));
  const lemon = new Color('#e9c640');
  for (let k = 0; k < 7; k++) {
    const a = hash01(v + 7, k) * Math.PI * 2;
    const e = -0.2 + hash01(v + 9, k) * 0.9;
    b.add(new TetrahedronGeometry(0.22, 0), lemon, new Matrix4().makeTranslation(Math.cos(a) * 1.72 * Math.cos(e), 2.3 + Math.sin(e) * 1.4, Math.sin(a) * 1.68 * Math.cos(e)));
  }
  return b.toProto();
}

// The harbour (a setting with a sea).

/** A boat on the water: a fishing gozzo (v 0, 1) or a sailing boat (v 2), its waterline at 0. */
export function boatProto(v: number): Proto {
  const b = new GeoBuilder();
  const band = new Color(['#b8352b', '#3f6b8a', '#4f7a4a'][v % 3]);
  const hull = PAL.cream;
  const L = v === 2 ? 9 : 6;
  // The hull: a box with a pointed bow, a coloured band round it.
  b.add(new BoxGeometry(L, 1.1, 2.3), hull, trs(0, 0.25, 0));
  b.add(new BoxGeometry(L + 0.04, 0.32, 2.34), band, trs(0, 0.6, 0));
  const bow = new ConeGeometry(1.15, 2.2, 4);
  b.add(bow, hull, new Matrix4().makeTranslation(L / 2 + 1.0, 0.25, 0).multiply(new Matrix4().makeRotationZ(-Math.PI / 2)).multiply(new Matrix4().makeRotationY(Math.PI / 4)).multiply(trs(0, 0, 0, 0, 1, 1, 0.55)));
  if (v === 2) {
    b.add(new CylinderGeometry(0.09, 0.12, 10, 5), PAL.cream, trs(0.6, 5.5, 0));
    // The sail: a thin triangle.
    const sail = new BufferGeometry();
    sail.setAttribute('position', new Float32BufferAttribute([0.7, 1.4, 0, 0.7, 10, 0, -3.6, 1.4, 0, 0.7, 1.4, 0, -3.6, 1.4, 0, 0.7, 10, 0], 3));
    b.add(sail, mix('cream', 'ochre', 0.12));
  } else {
    b.add(new BoxGeometry(1.6, 1.0, 1.4), mix('cream', 'asphalt', 0.1), trs(-0.8, 1.2, 0));
  }
  return b.toProto();
}

/** A row of tall, narrow seafront houses in the setting's colours (Portofino's waterfront), along x, facing +z. */
export function seafrontProto(seed: number): Proto {
  const b = new GeoBuilder();
  const walls = houses().walls;
  let x = 0;
  for (let k = 0; k < 8; k++) {
    const w = 5.5 + hash01(seed, k) * 2.5;
    const h = 10 + hash01(seed + 1, k) * 6;
    houseInto(b, x + w / 2, 0, w, 9, h, 0, walls[(k + seed) % walls.length], true, seed + k);
    x += w + 0.05;
  }
  return b.toProto();
}

/** The harbour light at the end of the breakwater: a white tower, red bands, a lantern. */
export function lighthouseProto(): Proto {
  const b = new GeoBuilder();
  b.add(new CylinderGeometry(3.2, 3.6, 2, 8), houses().stone, trs(0, 1, 0));
  for (let k = 0; k < 4; k++) b.add(new CylinderGeometry(1.45 - k * 0.08, 1.55 - k * 0.08, 3.2, 8), k % 2 ? PAL.red : PAL.cream, trs(0, 2 + 1.6 + k * 3.2, 0));
  b.add(new CylinderGeometry(1.0, 1.0, 1.6, 8), mix('cream', 'ochre', 0.4), trs(0, 15.8 + 0.8, 0));
  b.add(new ConeGeometry(1.3, 1.6, 8), PAL.red, trs(0, 18.2, 0));
  return b.toProto();
}

/** A piece of breakwater: rough stone blocks, 8 m along x. */
export function breakwaterProto(v: number): Proto {
  const b = new GeoBuilder();
  b.add(jitter(new BoxGeometry(8.4, 3.2, 5), 0.6, 190 + v), houses().stone, trs(0, 0.6, 0));
  b.add(jitter(new BoxGeometry(3, 1.4, 2.4), 0.4, 195 + v), mix('asphalt', 'cream', 0.42), trs(-1.5, 2.4, 0.6, v));
  return b.toProto();
}

function tuft(b: GeoBuilder, x: number, z: number, s: number, seed: number): void {
  const c = mix('olive', 'grass', 0.2);
  for (let k = 0; k < 3; k++) {
    const m = new Matrix4()
      .makeTranslation(x + (hash01(seed, k) - 0.5) * 0.3, 0.2 * s, z + (hash01(seed + 1, k) - 0.5) * 0.3)
      .multiply(new Matrix4().makeRotationZ((hash01(seed + 2, k) - 0.5) * 0.9));
    b.add(new ConeGeometry(0.07 * s, 0.45 * s, 3), c, m);
  }
}

// ---------------------------------------------------------------------------
// Hay, walls, fences
// ---------------------------------------------------------------------------

function baleInto(b: GeoBuilder, m: Matrix4): void {
  const straw = PAL.grass;
  const face = mix('grass', 'cream', 0.35);
  const ring = mix('grass', 'terracotta', 0.22);
  const side = new CylinderGeometry(0.72, 0.72, 1.25, 12, 1, true);
  const rot = new Matrix4().makeRotationZ(Math.PI / 2);
  b.add(side, straw, m.clone().multiply(trs(0, 0.72, 0)).multiply(rot), false);
  for (const s of [-1, 1]) {
    const cap = new CircleGeometry(0.72, 12);
    const capM = m.clone().multiply(trs(s * 0.625, 0.72, 0)).multiply(new Matrix4().makeRotationY((s * Math.PI) / 2));
    b.add(cap, face, capM);
    b.add(new CircleGeometry(0.42, 12), ring, capM.clone().multiply(trs(0, 0, 0.01)));
    b.add(new CircleGeometry(0.14, 8), face, capM.clone().multiply(trs(0, 0, 0.02)));
  }
}

export function baleProto(): Proto {
  const b = new GeoBuilder();
  baleInto(b, new Matrix4());
  return b.toProto();
}

export function baleStackProto(): Proto {
  const b = new GeoBuilder();
  baleInto(b, trs(0, 0, -0.74));
  baleInto(b, trs(0, 0, 0.74));
  baleInto(b, trs(0, 1.24, 0));
  return b.toProto();
}

/** Cut logs, stacked (the Dolomites' stand-in for a hay bale): no more triangles than the bale. */
function logsInto(b: GeoBuilder, m: Matrix4, rows: number[]): void {
  const bark = look().wood;
  const end = mix('ochre', 'cream', 0.35);
  const rot = new Matrix4().makeRotationZ(Math.PI / 2);
  let y = 0.24;
  rows.forEach((n, r) => {
    for (let k = 0; k < n; k++) {
      const z = (k - (n - 1) / 2) * 0.5;
      const at = m.clone().multiply(trs(0, y, z)).multiply(rot);
      b.add(new CylinderGeometry(0.24, 0.24, 1.9, 5, 1, true), bark, at);
      for (const s of [-1, 1]) b.add(new CircleGeometry(0.24, 5), end, m.clone().multiply(trs(s * 0.95, y, z)).multiply(new Matrix4().makeRotationY((s * Math.PI) / 2)));
    }
    y += 0.42;
    void r;
  });
}

export function logsProto(): Proto {
  const b = new GeoBuilder();
  logsInto(b, new Matrix4(), [2, 1]);
  return b.toProto();
}

export function logStackProto(): Proto {
  const b = new GeoBuilder();
  logsInto(b, new Matrix4(), [4, 3, 2]);
  return b.toProto();
}

/** One ~4 m stretch of dry-stone wall, centred, running along +x. */
export function wallProto(v: number): Proto {
  const b = new GeoBuilder();
  const L = 4.3;
  const rows = [0.24, 0.66, 1.02];
  rows.forEach((y, r) => {
    let x = -L / 2 + (r % 2) * 0.35;
    let k = 0;
    while (x < L / 2) {
      const w = Math.min(0.75 + hash01(v * 31 + r, k) * 0.7, L / 2 - x);
      if (w < 0.2) break;
      const h = r === 2 ? 0.34 : 0.42;
      const cols = look().walls.stones;
      const c = cols[Math.floor(hash01(v * 7 + k, r) * 3)];
      b.add(jitter(new BoxGeometry(w - 0.06, h, 0.62 - r * 0.06), 0.05, v * 100 + r * 10 + k), c, trs(x + w / 2, y, (hash01(k, r + v) - 0.5) * 0.08));
      x += w;
      k++;
    }
  });
  tuft(b, -1.2, 0.42, 1, v * 3);
  tuft(b, 1.4, -0.4, 0.8, v * 3 + 1);
  return b.toProto();
}

export function fenceProto(): Proto {
  const b = new GeoBuilder();
  const L = 4.1;
  const wood = look().wood;
  for (const x of [-L / 2, 0, L / 2]) b.add(new BoxGeometry(0.13, 1.15, 0.13), wood, trs(x, 0.575, 0));
  b.add(new BoxGeometry(L, 0.1, 0.07), wood, trs(0, 0.58, 0));
  b.add(new BoxGeometry(L, 0.1, 0.07), wood, trs(0, 0.98, 0));
  return b.toProto();
}

// ---------------------------------------------------------------------------
// Buildings
// ---------------------------------------------------------------------------

/** Gable roof prism: ridge along x. */
function gable(w: number, d: number, h: number): BufferGeometry {
  const hx = w / 2;
  const hz = d / 2;
  const v = [
    // two slopes
    -hx, 0, hz, hx, 0, hz, hx, h, 0,
    -hx, 0, hz, hx, h, 0, -hx, h, 0,
    hx, 0, -hz, -hx, 0, -hz, -hx, h, 0,
    hx, 0, -hz, -hx, h, 0, hx, h, 0,
    // gable ends
    hx, 0, hz, hx, 0, -hz, hx, h, 0,
    -hx, 0, -hz, -hx, 0, hz, -hx, h, 0,
  ];
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(v, 3));
  return g;
}

function houseInto(b: GeoBuilder, x: number, z: number, w: number, d: number, h: number, yaw: number, wall: Color, withDetails: boolean, seed: number): void {
  const m = trs(x, 0, z, yaw);
  b.add(new BoxGeometry(w, h, d), wall, m.clone().multiply(trs(0, h / 2, 0)));
  const H = houses();
  b.add(gable(w + 0.7, d + 0.8, Math.min(w, d) * H.roofPitch), H.roof, m.clone().multiply(trs(0, h, 0)));
  if (!withDetails) return;
  const shutter = H.shutter;
  const n = Math.max(1, Math.floor(w / 3));
  for (let i = 0; i < n; i++) {
    const wx = -w / 2 + (i + 0.5) * (w / n);
    for (const s of [-1, 1]) {
      if (h > 4.5) b.add(new BoxGeometry(0.9, 1.05, 0.12), shutter, m.clone().multiply(trs(wx, h * 0.7, s * (d / 2 + 0.05))));
      if (i !== Math.floor(n / 2) || s < 0) b.add(new BoxGeometry(0.8, 0.95, 0.12), shutter, m.clone().multiply(trs(wx, h * 0.3, s * (d / 2 + 0.05))));
    }
  }
  b.add(new BoxGeometry(1.2, 2.1, 0.14), H.door, m.clone().multiply(trs(-w / 2 + (Math.floor(n / 2) + 0.5) * (w / n), 1.05, d / 2 + 0.06)));
  if (hash01(seed, 3) > 0.3) {
    b.add(new BoxGeometry(0.8, 1.8, 0.8), wall, m.clone().multiply(trs(w * 0.25, h + Math.min(w, d) * 0.2, 0)));
    b.add(new BoxGeometry(1.0, 0.18, 1.0), H.roof, m.clone().multiply(trs(w * 0.25, h + Math.min(w, d) * 0.2 + 0.95, 0)));
  }
}

export function farmhouseProto(v: number): Proto {
  const b = new GeoBuilder();
  const [wallA, wallB] = houses().farm;
  houseInto(b, 0, 0, 11, 8, 6.2, 0, wallA, true, v);
  houseInto(b, v === 0 ? 7.6 : -7.6, 1, 5.5, 6.5, 4.2, Math.PI / 2, v === 0 ? wallB : wallA, true, v + 5);
  // A low garden wall and a couple of pots.
  b.add(new BoxGeometry(14, 0.9, 0.5), houses().stone, trs(0, 0.45, 7.5));
  return b.toProto();
}

/** A hilltop town: a tight cluster of houses and one tower. */
export function townProto(seed: number): Proto {
  const b = new GeoBuilder();
  const H = houses();
  const walls = H.walls;
  // Town walls ring (low, stone).
  for (let k = 0; k < 22; k++) {
    const a = (k / 22) * Math.PI * 2;
    b.add(new BoxGeometry(14, 5, 2), H.stone, trs(Math.cos(a) * 48, 1, Math.sin(a) * 48, -a + Math.PI / 2));
  }
  for (let k = 0; k < 17; k++) {
    const a = hash01(seed, k) * Math.PI * 2;
    const r = 8 + hash01(seed + 1, k) * 34;
    const w = 7 + hash01(seed + 2, k) * 6;
    const d = 6 + hash01(seed + 3, k) * 4;
    const h = 5 + hash01(seed + 4, k) * 5 + (r < 20 ? 3 : 0);
    houseInto(b, Math.cos(a) * r, Math.sin(a) * r, w, d, h, hash01(seed + 5, k) * Math.PI, walls[k % walls.length], false, seed + k);
  }
  const tx = 4;
  const tz = -3;
  const tower = look().tower;
  if (tower !== 'tuscan') {
    towerOther(b, tower, tx, tz, H);
    return b.toProto();
  }
  // The tower with merlons.
  b.add(new BoxGeometry(6, 27, 6), H.tower, trs(tx, 13.5, tz));
  b.add(new BoxGeometry(6.8, 1, 6.8), H.towerTop, trs(tx, 27, tz));
  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 3; j++) {
      const o = -2.6 + j * 2.6;
      const pos: [number, number][] = [[o, -3.1], [o, 3.1], [-3.1, o], [3.1, o]];
      const [mx, mz] = pos[i];
      b.add(new BoxGeometry(1.2, 1.4, 1.2), H.towerTop, trs(tx + mx, 28.2, tz + mz));
    }
  }
  b.add(new BoxGeometry(1.4, 2.4, 0.3), PAL.brown, trs(tx, 22, tz + 3.05));
  return b.toProto();
}

/** The town's landmark in the other settings: Genoa's striped bell tower, an alpine church spire, a baroque dome. */
function towerOther(b: GeoBuilder, kind: 'striped' | 'spire' | 'dome', tx: number, tz: number, H: ReturnType<typeof houses>): void {
  if (kind === 'striped') {
    // Bands of dark and pale stone, an open belfry, a pyramid roof.
    for (let k = 0; k < 9; k++) b.add(new BoxGeometry(5.4, 2.6, 5.4), k % 2 ? mix('brown', 'asphalt', 0.4) : PAL.cream, trs(tx, 1.3 + k * 2.6, tz));
    b.add(new BoxGeometry(5.8, 0.6, 5.8), PAL.cream, trs(tx, 23.7, tz));
    for (const [mx, mz] of [[-2.3, -2.3], [2.3, -2.3], [-2.3, 2.3], [2.3, 2.3]]) b.add(new BoxGeometry(0.9, 3.4, 0.9), PAL.cream, trs(tx + mx, 25.7, tz + mz));
    b.add(new ConeGeometry(4.3, 5.5, 4), H.roof, trs(tx, 30.2, tz, Math.PI / 4));
  } else if (kind === 'spire') {
    // A white tower, a clock face, a tall pointed spire.
    b.add(new BoxGeometry(5.5, 22, 5.5), H.tower, trs(tx, 11, tz));
    b.add(new BoxGeometry(5.9, 0.6, 5.9), H.towerTop, trs(tx, 22.3, tz));
    b.add(new CylinderGeometry(1.2, 1.2, 0.2, 10), PAL.cream, trs(tx, 17, tz + 2.85, 0).multiply(new Matrix4().makeRotationX(Math.PI / 2)));
    b.add(new ConeGeometry(3.9, 15, 8), H.roof, trs(tx, 30.1, tz));
    b.add(new BoxGeometry(0.2, 2, 0.2), PAL.ochre, trs(tx, 38.4, tz));
  } else {
    // A drum, a dome, a lantern on top.
    b.add(new BoxGeometry(12, 11, 12), H.tower, trs(tx, 5.5, tz));
    b.add(new CylinderGeometry(4.6, 4.8, 5, 10), H.towerTop, trs(tx, 13.5, tz));
    b.add(new SphereGeometry(4.6, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2), H.roof, trs(tx, 16, tz));
    b.add(new CylinderGeometry(0.9, 1.0, 2.6, 8), PAL.cream, trs(tx, 21.6, tz));
    b.add(new ConeGeometry(1.1, 1.6, 8), H.roof, trs(tx, 23.7, tz));
  }
}

// ---------------------------------------------------------------------------
// Circuit furniture
// ---------------------------------------------------------------------------

/** Grandstand facing +x, length along z. Crowd is added separately (animated). */
export function grandstandProto(length: number, depth: number): Proto {
  const b = new GeoBuilder();
  const L = length;
  const D = depth;
  const back = -D / 2;
  const front = D / 2;
  // Plinth and tiers.
  b.add(new BoxGeometry(D, 1.3, L), STONE_A, trs(0, 0.65, 0));
  for (let k = 0; k < 4; k++) {
    const x0 = front - 1.5 - k * 1.7;
    b.add(new BoxGeometry(1.7, 0.5, L - 0.6), k % 2 ? mix('asphalt', 'brown', 0.35) : mix('asphalt', 'brown', 0.2), trs(x0 - 0.85 + 0.85, 1.3 + k * 0.62, 0));
  }
  // Back wall.
  const wood = look().wood;
  const stand = look().stand;
  b.add(new BoxGeometry(0.3, 4.2, L), wood, trs(back + 0.15, 2.1 + 1.3, 0));
  // Posts.
  const posts = Math.round(L / 7.5);
  for (let i = 0; i <= posts; i++) {
    const z = -L / 2 + (i * L) / posts;
    b.add(new BoxGeometry(0.28, 6.6, 0.28), wood, trs(front - 0.4, 3.3, z));
    b.add(new BoxGeometry(0.28, 7.4, 0.28), wood, trs(back + 0.3, 3.7, z));
  }
  // Front banners: green / cream / red.
  const panels = Math.floor(L / 3.4);
  for (let i = 0; i < panels; i++) {
    const z = -L / 2 + (i + 0.5) * (L / panels);
    if (i % 3 === 1) {
      const w = (L / panels) * 0.86;
      b.add(new BoxGeometry(0.1, 1.05, w / 3), stand.banner[0], trs(front + 0.06, 0.75, z - w / 3));
      b.add(new BoxGeometry(0.1, 1.05, w / 3), stand.banner[1], trs(front + 0.06, 0.75, z));
      b.add(new BoxGeometry(0.1, 1.05, w / 3), stand.banner[2], trs(front + 0.06, 0.75, z + w / 3));
    } else {
      b.add(new BoxGeometry(0.1, 1.05, (L / panels) * 0.9), mix('cream', 'asphalt', 0.12), trs(front + 0.06, 0.75, z));
    }
  }
  // Striped awning, sloping down toward the track, stripes running front-to-back.
  const stripes = Math.round(L / 1.6);
  const roofBack = 7.6;
  const roofFront = 6.4;
  const overhang = 1.4;
  for (let i = 0; i < stripes; i++) {
    const z0 = -L / 2 + (i * L) / stripes;
    const z1 = z0 + L / stripes;
    const col = i % 2 === 0 ? stand.stripeA : stand.stripeB;
    const x0 = back - 0.3;
    const x1 = front + overhang;
    const tri = [x0, roofBack, z0, x1, roofFront, z0, x1, roofFront, z1, x0, roofBack, z0, x1, roofFront, z1, x0, roofBack, z1];
    b.addTriangles(tri, col);
    b.addTriangles([x0, roofBack, z0, x1, roofFront, z1, x1, roofFront, z0, x0, roofBack, z0, x0, roofBack, z1, x1, roofFront, z1], col);
    // Scalloped valance.
    const zm = (z0 + z1) / 2;
    const v = [x1, roofFront, z0, x1, roofFront - 0.75, zm, x1, roofFront, z1];
    b.addTriangles(v, col);
    b.addTriangles([x1, roofFront, z0, x1, roofFront, z1, x1, roofFront - 0.75, zm], col);
  }
  // Flags along the roof.
  for (let i = 0; i < 6; i++) {
    const z = -L / 2 + ((i + 0.5) * L) / 6;
    flagInto(b, trs(back + 0.2, roofBack, z), i % 2, 2.2);
  }
  return b.toProto();
}

function flagInto(b: GeoBuilder, m: Matrix4, v: number, pole = 4.5): void {
  b.add(new CylinderGeometry(0.05, 0.06, pole, 5), PAL.cream, m.clone().multiply(trs(0, pole / 2, 0)));
  const col = v === 0 ? PAL.red : PAL.cream;
  // A little wave: three bent panels.
  const pts: number[] = [];
  const w = 1.5;
  const h = 0.9;
  for (let k = 0; k < 3; k++) {
    const x0 = (k / 3) * w;
    const x1 = ((k + 1) / 3) * w;
    const z0 = Math.sin(k * 1.6) * 0.18;
    const z1 = Math.sin((k + 1) * 1.6) * 0.18;
    const y1 = pole;
    const y0 = pole - h;
    pts.push(-x0, y0, z0, -x1, y0, z1, -x1, y1, z1, -x0, y0, z0, -x1, y1, z1, -x0, y1, z0);
    pts.push(-x0, y0, z0, -x1, y1, z1, -x1, y0, z1, -x0, y0, z0, -x0, y1, z0, -x1, y1, z1);
  }
  const e = m.elements;
  const world: number[] = [];
  for (let i = 0; i < pts.length; i += 3) {
    const x = pts[i], y = pts[i + 1], z = pts[i + 2];
    world.push(e[0] * x + e[4] * y + e[8] * z + e[12], e[1] * x + e[5] * y + e[9] * z + e[13], e[2] * x + e[6] * y + e[10] * z + e[14]);
  }
  b.addTriangles(world, col);
}

export function flagProto(v: number): Proto {
  const b = new GeoBuilder();
  flagInto(b, new Matrix4(), v);
  return b.toProto();
}

/** Start/finish gantry spanning `span` metres across z, banner checkered. */
export function gantryProto(span: number): Proto {
  const b = new GeoBuilder();
  const H = 6.2;
  for (const s of [-1, 1]) {
    const z = (s * span) / 2;
    b.add(new BoxGeometry(1.5, 0.9, 1.5), CONCRETE, trs(0, 0.45, z));
    for (const [dx, dz] of [[-0.42, -0.42], [0.42, -0.42], [-0.42, 0.42], [0.42, 0.42]]) {
      b.add(new BoxGeometry(0.15, H, 0.15), PAL.brown, trs(dx, 0.9 + H / 2, z + dz));
    }
    // Lattice bracing on the faces.
    for (let k = 0; k < 5; k++) {
      const y = 1.4 + k * 1.15;
      for (const face of [-0.42, 0.42]) {
        const m = trs(face, y, z, 0).multiply(new Matrix4().makeRotationX(k % 2 ? 0.95 : -0.95));
        b.add(new BoxGeometry(0.08, 1.35, 0.08), PAL.brown, m);
        const m2 = trs(0, y, z + face).multiply(new Matrix4().makeRotationZ(k % 2 ? 0.95 : -0.95));
        b.add(new BoxGeometry(1.35, 0.08, 0.08), PAL.brown, m2);
      }
    }
  }
  // Top beam and the chequered banner (both faces).
  const top = 0.9 + H;
  b.add(new BoxGeometry(0.4, 0.3, span + 1.2), PAL.brown, trs(0, top, 0));
  b.add(new BoxGeometry(0.4, 0.3, span + 1.2), PAL.brown, trs(0, top - 1.75, 0));
  const cols = Math.round(span / 0.72);
  const sq = span / cols;
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < cols; c++) {
      const col = (r + c) % 2 === 0 ? PAL.cream : PAL.brown;
      const z = -span / 2 + (c + 0.5) * sq;
      const y = top - 0.5 - r * 0.72;
      b.add(new BoxGeometry(0.12, 0.72, sq), col, trs(0, y, z));
    }
  }
  return b.toProto();
}

/** Posts and back of a roadside board (the printed face is a separate textured quad). */
export function boardFrameProto(w: number, h: number, lift: number, postH: number): Proto {
  const b = new GeoBuilder();
  for (const s of [-1, 1]) b.add(new BoxGeometry(0.14, postH, 0.14), PAL.brown, trs(-0.1, postH / 2, (s * w) / 2.6));
  b.add(new BoxGeometry(0.08, h + 0.12, w + 0.12), PAL.brown, trs(-0.06, lift + h / 2, 0));
  tuft(b, 0, (w / 2.6) * 0.9, 1, 5);
  tuft(b, 0, -(w / 2.6) * 0.9, 1, 6);
  return b.toProto();
}

/** A textured face quad facing +x, UVs in [u0,v0,u1,v1]. */
export function faceQuad(w: number, h: number, lift: number, uv: [number, number, number, number]): PlaneGeometry {
  const g = new PlaneGeometry(w, h);
  g.rotateY(Math.PI / 2);
  g.translate(0.0, lift + h / 2, 0);
  const a = g.getAttribute('uv');
  // PlaneGeometry after rotateY(+90deg): its +u points toward -z. Flip to read left-to-right from +x.
  for (let i = 0; i < a.count; i++) {
    const u = a.getX(i);
    const v = a.getY(i);
    a.setXY(i, uv[0] + (uv[2] - uv[0]) * u, uv[1] + (uv[3] - uv[1]) * v);
  }
  return g;
}
